import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Stack, Typography, TextField, Autocomplete, Select, MenuItem,
  FormControl, IconButton, Tooltip, Checkbox, FormControlLabel, Divider,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useData } from '../context/DataContext';
import type { Quote } from '../data/types';
import { BRAND, BRAND_LIGHT, BRAND_BORDER, INK, INK_SOFT } from '../theme/tokens';

const INVOICE_TERMS = ['7 Day Terms', '14 Day Terms', '30 Days EOM', '30 Days End of Month', '60 Days EOM', 'COD', 'Payment on Completion'];
const PROBABILITIES = ['0%', '10%', '25%', '50%', '70%', '90%', '100%'];
const DIVISIONS = ['Service Div', 'Field Div', 'Projects Div', 'Admin'];

const labelSx = {
  fontSize: '0.62rem',
  fontWeight: 700,
  color: INK_SOFT,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.4,
  mb: 0.4,
  display: 'block',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: BRAND_LIGHT,
    borderRadius: 1,
    fontSize: '0.82rem',
    '& fieldset':             { borderColor: 'transparent' },
    '&:hover fieldset':       { borderColor: BRAND_BORDER },
    '&.Mui-focused fieldset': { borderColor: BRAND, borderWidth: 1 },
  },
  '& .MuiInputBase-input': { py: 0.7, px: 1, color: INK, fontWeight: 500 },
  '& .MuiSelect-select':   { py: 0.7, px: 1 },
};

const factSx = { fontSize: '0.72rem', color: INK_SOFT, lineHeight: 1.5 };

interface QuoteEditFormProps {
  quote: Quote;
}

export const QuoteEditForm: React.FC<QuoteEditFormProps> = ({ quote }) => {
  const { sites, projects, updateQuote } = useData();

  const [draft, setDraft] = useState<Quote>(quote);
  useEffect(() => { setDraft(quote); }, [quote.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitTimer = useRef<number | null>(null);
  useEffect(() => {
    if (commitTimer.current) window.clearTimeout(commitTimer.current);
    const drift = Object.keys(draft).some(k => (draft as any)[k] !== (quote as any)[k]);
    if (!drift) return;
    commitTimer.current = window.setTimeout(() => {
      const updates: Partial<Quote> = {};
      Object.keys(draft).forEach(k => {
        if ((draft as any)[k] !== (quote as any)[k]) (updates as any)[k] = (draft as any)[k];
      });
      if (Object.keys(updates).length > 0) updateQuote(quote.id, updates);
    }, 400);
    return () => { if (commitTimer.current) window.clearTimeout(commitTimer.current); };
  }, [draft, quote, updateQuote]);

  const set = <K extends keyof Quote>(key: K, value: Quote[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  const customer = sites.find(s => s.id === draft.customerId);
  const project  = projects.find(p => p.id === draft.projectId);

  return (
    <Box sx={{ p: 2.5, backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 2.5 }}>
        <Section title="To">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Autocomplete
              size="small" fullWidth options={sites}
              getOptionLabel={s => s.businessName ?? s.name}
              value={customer ?? null}
              onChange={(_, v) => {
                set('customerId', v?.id);
                set('customerName', v?.businessName ?? v?.name);
                set('customerAddress', v?.address);
              }}
              renderInput={params => <TextField {...params} placeholder="Search customer" sx={inputSx} />}
            />
            <AddInlineButton tooltip="Create new customer" />
          </Stack>
          <FactGrid>
            <Fact label="Business name" value={customer?.businessName ?? draft.customerName} />
            <Fact label="Email"         value={customer?.email} />
            <Fact label="Address"       value={customer?.address ?? draft.customerAddress} />
          </FactGrid>
        </Section>

        <Section title="Contact">
          <TextField size="small" fullWidth placeholder="Contact name" sx={{ ...inputSx, mb: 1 }}
            value={draft.contactName ?? ''} onChange={e => set('contactName', e.target.value)} />
          <FactGrid>
            <Fact label="Name"    value={draft.contactName} />
            <Fact label="Email"   value={draft.contactEmail} />
            <Fact label="Address" value={draft.contactAddress} />
          </FactGrid>
        </Section>

        <Section title="Project">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Autocomplete
              size="small" fullWidth options={projects}
              getOptionLabel={p => p.name}
              value={project ?? null}
              onChange={(_, v) => set('projectId', v?.id)}
              renderInput={params => <TextField {...params} placeholder="Search project" sx={inputSx} />}
            />
            <Tooltip title="Open project in new tab" arrow>
              <span>
                <IconButton size="small" disabled={!project}
                  sx={{ color: BRAND, '&:hover': { backgroundColor: BRAND_LIGHT }, '&.Mui-disabled': { color: '#ccc' } }}>
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <AddInlineButton tooltip="Create new project" />
          </Stack>
          <FactGrid>
            <Fact label="Project name"   value={project?.name} />
            <Fact label="Project number" value={project?.number} />
            <Fact label="Start date"     value={project?.startDate} />
            <Fact label="End date"       value={project?.endDate} />
          </FactGrid>
        </Section>
      </Box>

      <Divider sx={{ my: 2.5 }} />

      <Section title="Details">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mt: 1 }}>
          <Field label="Title">
            <TextField size="small" fullWidth sx={inputSx} value={draft.title ?? ''} onChange={e => set('title', e.target.value)} />
          </Field>
          <Field label="Invoice term">
            <SelectField value={draft.invoiceTerm ?? ''} onChange={v => set('invoiceTerm', v)} options={INVOICE_TERMS} allowEmpty />
          </Field>
          <Field label="Expiry date">
            <DateField value={draft.expiryDate} onChange={v => set('expiryDate', v)} />
          </Field>
          <Field label="Valid date">
            <DateField value={draft.validDate} onChange={v => set('validDate', v)} />
          </Field>

          <Field label="Accepted date">
            <DateField value={draft.acceptedDate} onChange={v => set('acceptedDate', v)} />
          </Field>
          <Field label="Purchase order number">
            <TextField size="small" fullWidth sx={inputSx} value={draft.purchaseOrderNumber ?? ''} onChange={e => set('purchaseOrderNumber', e.target.value)} />
          </Field>
          <Field label="Quote date" required>
            <DateField value={draft.quoteDate} onChange={v => set('quoteDate', v ?? '')} />
          </Field>

          <Field label="Owner / Sales Rep">
            <TextField size="small" fullWidth sx={inputSx} value={draft.ownerSalesRep ?? ''} onChange={e => set('ownerSalesRep', e.target.value)} />
          </Field>
          <Field label="Probability">
            <SelectField value={draft.probability ?? ''} onChange={v => set('probability', v)} options={PROBABILITIES} allowEmpty emptyLabel="—" />
          </Field>
          <Field label="Expected start date">
            <DateField value={draft.expectedStartDate} onChange={v => set('expectedStartDate', v)} />
          </Field>
          <Field label="Business entity">
            <TextField size="small" fullWidth sx={inputSx} value={draft.businessEntity ?? ''} onChange={e => set('businessEntity', e.target.value)} />
          </Field>

          <Field label="Division">
            <SelectField value={draft.division ?? ''} onChange={v => set('division', v)} options={DIVISIONS} allowEmpty />
          </Field>
          <Field label="Area">
            <TextField size="small" fullWidth sx={inputSx} value={draft.area ?? ''} onChange={e => set('area', e.target.value)} />
          </Field>
        </Box>

        <FormControlLabel sx={{ mt: 1 }}
          control={<Checkbox size="small" checked={!!draft.sent} onChange={e => set('sent', e.target.checked)}
            sx={{ color: BRAND, '&.Mui-checked': { color: BRAND } }} />}
          label={<Typography sx={{ fontSize: '0.78rem' }}>Sent</Typography>}
        />
      </Section>

      <Divider sx={{ my: 2.5 }} />

      <Section title="Notes">
        <Stack spacing={1.5}>
          <Field label="Revision notes">
            <TextField size="small" fullWidth multiline minRows={2} sx={inputSx} value={draft.revisionNotes ?? ''} onChange={e => set('revisionNotes', e.target.value)} />
          </Field>
          <Field label="Internal notes">
            <TextField size="small" fullWidth multiline minRows={2} sx={inputSx} value={draft.internalNotes ?? ''} onChange={e => set('internalNotes', e.target.value)} />
          </Field>
          <Field label="Notes to customer">
            <TextField size="small" fullWidth multiline minRows={2} sx={inputSx} value={draft.notesToCustomer ?? ''} onChange={e => set('notesToCustomer', e.target.value)} />
          </Field>
          <Field label="Exclusions / Inclusions / Special Conditions">
            <TextField size="small" fullWidth multiline minRows={2} sx={inputSx} value={draft.exclusionsInclusions ?? ''} onChange={e => set('exclusionsInclusions', e.target.value)} />
          </Field>
        </Stack>
      </Section>

      <Typography sx={{ fontSize: '0.65rem', color: INK_SOFT, mt: 2, fontStyle: 'italic' }}>
        Changes save automatically.
      </Typography>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: INK, mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <Box>
    <InputLabel shrink sx={{ ...labelSx, position: 'static', transform: 'none' }}>
      {label}{required && <span style={{ color: BRAND, marginLeft: 2 }}>*</span>}
    </InputLabel>
    {children}
  </Box>
);

const Fact: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'baseline' }}>
    <Typography sx={{ fontSize: '0.7rem', color: INK_SOFT, fontWeight: 600, minWidth: 96 }}>{label}:</Typography>
    <Typography sx={factSx}>{value && value.trim() ? value : '—'}</Typography>
  </Box>
);

const FactGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 0.4, pl: 0.5 }}>{children}</Box>
);

const SelectField: React.FC<{
  value: string; onChange: (v: string) => void; options: readonly string[];
  allowEmpty?: boolean; emptyLabel?: string;
}> = ({ value, onChange, options, allowEmpty, emptyLabel }) => (
  <FormControl size="small" fullWidth>
    <Select value={value} displayEmpty sx={inputSx} onChange={e => onChange(e.target.value)}>
      {allowEmpty && <MenuItem value=""><em>{emptyLabel ?? 'add'}</em></MenuItem>}
      {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </Select>
  </FormControl>
);

const DateField: React.FC<{ value?: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <TextField size="small" type="date" fullWidth sx={inputSx}
    value={value ?? ''} onChange={e => onChange(e.target.value)} InputLabelProps={{ shrink: true }} />
);

const AddInlineButton: React.FC<{ tooltip: string }> = ({ tooltip }) => (
  <Tooltip title={tooltip} arrow>
    <IconButton size="small"
      sx={{ backgroundColor: BRAND, color: '#fff', borderRadius: 1, p: 0.4, '&:hover': { backgroundColor: '#4a0a82' } }}>
      <AddIcon sx={{ fontSize: 18 }} />
    </IconButton>
  </Tooltip>
);

export default QuoteEditForm;
